import { faImage, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Button } from 'components/UI/Button/Button';
import React from 'react';
import classes from './RadioCard.module.scss';

const RADIO_CARD_MODES = {
    RADIO: 'radio',
    CHECKBOX: 'checkbox'
} as const;

type RadioCardMode = (typeof RADIO_CARD_MODES)[keyof typeof RADIO_CARD_MODES];

interface RadioCardProps {
    title: string;
    checked?: boolean;
    description?: string;
    content?: string;
    badge?: React.ReactNode;
    badgeClassName?: string;
    image?: string | null;
    mode?: RadioCardMode;
    // Checkbox mode label of the checked button. Defaults to "Added".
    checkedLabel?: string;
    // Already decided (an Add-On on the booking): the button keeps its look but the card ignores clicks.
    locked?: boolean;
    lockedLabel?: string;
    // Replaces the button while the card is checked (a quantity pill that gives the card back at 0).
    checkedAction?: React.ReactNode;
    className?: string;
    // Shown inside the card, under a divider, while it is checked.
    children?: React.ReactNode;
    onChange?: (checked: boolean) => void;
}

export const RadioCard: React.FC<RadioCardProps> = ({
    title,
    content,
    description,
    badge,
    badgeClassName,
    mode = RADIO_CARD_MODES.RADIO,
    image,
    checked,
    checkedLabel = 'Added',
    locked,
    lockedLabel,
    checkedAction,
    className,
    children,
    onChange
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const changeHandler = React.useCallback(() => {
        if (!locked) {
            onChange?.(!checked);
        }
    }, [checked, locked, onChange]);

    const isExpanded = Boolean(checked && children);

    return (
        <div className={clsx(classes.root, className, { [classes.expanded]: isExpanded })}>
            {image && (
                <img src={image} alt={title} className={classes.image} onClick={changeHandler} />
            )}

            {image === null && (
                <div
                    className={clsx(classes.image, classes.imagePlaceholder)}
                    onClick={changeHandler}
                >
                    <FontAwesomeIcon icon={faImage} />
                </div>
            )}

            <div className={classes.info}>
                <div className={classes.header}>
                    <div className={classes.headerContent}>
                        {badge && <p className={clsx(classes.badge, badgeClassName)}>{badge}</p>}

                        <p
                            className={clsx(classes.title, { [classes.titleLocked]: locked })}
                            onClick={changeHandler}
                        >
                            {title}
                        </p>

                        {description && <p className={classes.description}>{description}</p>}
                    </div>

                    {checked && checkedAction ? (
                        <div className={classes.action}>{checkedAction}</div>
                    ) : (
                        <Button
                            size="small"
                            variant={checked ? 'black' : 'outlined'}
                            className={classes.button}
                            aria-label={locked ? lockedLabel : undefined}
                            onClick={changeHandler}
                        >
                            {mode === RADIO_CARD_MODES.RADIO && (checked ? 'Selected' : 'Select')}

                            {mode === RADIO_CARD_MODES.CHECKBOX &&
                                (checked ? (
                                    checkedLabel
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPlus} /> Add
                                    </>
                                ))}
                        </Button>
                    )}
                </div>

                {isExpanded && <div className={classes.expandedContent}>{children}</div>}

                {content && isOpen && <p className={classes.content}>{content}</p>}

                {content && (
                    <div>
                        <button
                            type="button"
                            className={classes.toggle}
                            aria-label={content ? 'Hide info' : 'More info'}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? 'Hide info' : 'More info'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
